import { useEffect, useMemo, useState } from "react"

import { useLista, useManagedContext } from "../../hooks"
import { SolicitudFirmaModal, TableCustom } from "../../common"
import { standardRequest } from "../../../utils/requests"
import { ALERT_TYPE, CUENTA_STATE, ESTADOS_FIRMA, FIRMA_TYPE, REQUEST_METHOD, ShowToastMessage, TRIBUTO_TYPE } from '@laramie/ui';
import { APIS } from "../../../config/apis"
import { SelectTipoFirmaModal } from "./components"
import { MEMO_FIRMA_LISTAS } from "./consts"
import { getDateToString } from "../../../utils/convert"
const CertificadosGrid = ({ entity, pendingChange, onCertificadoAdded, disabled }) => {
    const { setIsLoading } = useManagedContext()

    const [firmas, setFirmas] = useState([])
    const [modalItem, setModalItem] = useState(null)
    const [showSelectTipoModal, setShowSelectTipoModal] = useState(false)

    const [getListLista, getRowLista] = useLista({
        listas: ["TipoFirma", "EstadoFirma"],
        onLoaded: (listas, isSuccess, error) => {
            if (!isSuccess) ShowToastMessage(ALERT_TYPE.ALERT_ERROR, error)
        },
        memo: {
          key: MEMO_FIRMA_LISTAS,
          timeout: 0
        }
    });

    const tributo = entity.cuenta.idTipoTributo === TRIBUTO_TYPE.INMUEBLES ? entity.inmueble : 
                    entity.cuenta.idTipoTributo === TRIBUTO_TYPE.VEHICULOS ? entity.vehiculo : { id: 0 };

    const selectableIds = useMemo(() => {
        let ids = []

        if (!firmas.some(firma => firma.idTipoFirma === FIRMA_TYPE.CERTIFICADO_LIBRE_DEUDA && firma.idEstadoFirma === ESTADOS_FIRMA.PENDIENTE)) {
            ids.push(FIRMA_TYPE.CERTIFICADO_LIBRE_DEUDA)
        }

        if (entity.cuenta.idEstadoCuenta === CUENTA_STATE.ACTIVA) {
            if (entity.cuenta.idTipoTributo === TRIBUTO_TYPE.INMUEBLES) {
                if (!firmas.some(firma => firma.idTipoFirma === FIRMA_TYPE.CERTIFICADO_DEUDA_INMUEBLE && firma.idEstadoFirma === ESTADOS_FIRMA.PENDIENTE)) {
                    ids.push(FIRMA_TYPE.CERTIFICADO_DEUDA_INMUEBLE)
                }
                if (!firmas.some(firma => firma.idTipoFirma === FIRMA_TYPE.CERTIFICADO_CATASTRAL && firma.idEstadoFirma === ESTADOS_FIRMA.PENDIENTE)) {
                    ids.push(FIRMA_TYPE.CERTIFICADO_CATASTRAL)
                }
            }

            if (entity.cuenta.idTipoTributo === TRIBUTO_TYPE.VEHICULOS) {
                if (!firmas.some(firma => firma.idTipoFirma === FIRMA_TYPE.CERTIFICADO_BAJA_AUTOMOTOR && firma.idEstadoFirma === ESTADOS_FIRMA.PENDIENTE)) {
                    ids.push(FIRMA_TYPE.CERTIFICADO_BAJA_AUTOMOTOR)
                }
            }
        }
        else {
            if (entity.cuenta.idTipoTributo === TRIBUTO_TYPE.VEHICULOS) {
                if (!firmas.some(firma => firma.idTipoFirma === FIRMA_TYPE.CERTIFICADO_BAJA_AUTOMOTOR && firma.idEstadoFirma === ESTADOS_FIRMA.PENDIENTE)) {
                    ids.push(FIRMA_TYPE.CERTIFICADO_BAJA_AUTOMOTOR)
                }
            }
        }

        return ids
    }, [firmas])

    useEffect(() => {
        setIsLoading(true)

        const ids = [tributo.idFirmaDigitalBaja, entity.cuenta.idFirmaDigitalLibreDeuda,
                     tributo.idFirmaDigitalCertificadoDeuda, tributo.idFirmaDigitalCertificadoCatastral].filter(x => x)
        Promise.all(ids.map(id => standardRequest(REQUEST_METHOD.GET, `${APIS.URLS.FIRMA_DIGITAL}/${id}`)))
        .then(results => setFirmas(results))
        .catch(() => {})
        .finally(() => setIsLoading(false))
    }, [tributo.idFirmaDigitalBaja, entity.cuenta.idFirmaDigitalLibreDeuda,
        tributo.idFirmaDigitalCertificadoDeuda, tributo.idFirmaDigitalCertificadoCatastral])

    const onClickAdd = async () => {
        if (pendingChange) {
            ShowToastMessage(ALERT_TYPE.ALERT_ERROR, "Debe guardar los cambios antes de crear un certificado")
        }
        else setShowSelectTipoModal(true)
    }

    const onClickDownload = async ({ id }) => {
        setIsLoading(true)
        standardRequest(REQUEST_METHOD.GET, `${APIS.URLS.FIRMA_DIGITAL}/${id}/signed-file`)
        .then(({ file64 }) => {
            const a = document.createElement("a")
            a.href = "data:application/pdf;base64," + file64
            a.download = "documento.pdf"
            a.click()
        })
        .catch(() => {})
        .finally(() => setIsLoading(false))
    }

    const onSelectTipoFirmaAccept = () => {
        setShowSelectTipoModal(false)    
        onCertificadoAdded()
    }

    const columns = [
        {
            Header: 'Tipo de certificado',
            Cell: ({ value }) => getRowLista("TipoFirma", value)?.nombre ?? '',
            accessor: 'idTipoFirma',
        },{
            Header: 'Fecha de solicitud',
            Cell: ({ value }) => getDateToString(value),
            accessor: 'fechaSolicitud'
        },{
            Header: 'Estado',
            Cell: ({ value }) => getRowLista("EstadoFirma", value)?.nombre ?? '',
            accessor: 'idEstadoFirma',
        },{
            id:'abm', accessor: 'id', width: '5%', disableGlobalFilter: true, disableSortBy: true,
            Header: () => selectableIds.length < 1 || disabled ? '' : (
                <div className='action'>
                    <div onClick={onClickAdd} className="link">
                        <span className="material-symbols-outlined" title="Nuevo">add</span>
                    </div>
                </div>
            ),
            Cell: ({ row }) =>  (
                <div className='action'>
                    <div onClick={() => setModalItem(row.original)} className="link">
                        <span className="material-symbols-outlined" title="Ver">search</span>
                    </div>
                    <div className="link" onClick={() => onClickDownload(row.original)}>
                        <span className="material-symbols-outlined" title="Descargar CSV">download</span>
                    </div>
                </div>
            ),
        }
    ]

    return (
        <>
            {showSelectTipoModal && (
                <SelectTipoFirmaModal
                    idCuenta={entity.cuenta.id}
                    idTributo={tributo.id}
                    selectableIds={selectableIds}
                    onCertificadoAdded={onSelectTipoFirmaAccept}
                    close={() => setShowSelectTipoModal(false)}
                />
            )}

            {!!modalItem && (
                <SolicitudFirmaModal data={modalItem} onDismiss={() => setModalItem(null)} />
            )}

            <TableCustom
                showFilterGlobal={false}
                className="TableCustomBase"
                data={firmas}
                columns={columns}
            />
        </>
    )
}

export default CertificadosGrid
